import { BaseError, type BaseErrorOptions } from './error';

type WebGLErrorReason = string & { __webglErrorReason: never };

export function getWebGLError(
  gl: WebGL2RenderingContext,
  errorCode: number,
): WebGLErrorReason {
  switch (errorCode) {
    case gl.NO_ERROR:
      return 'No error' as WebGLErrorReason;
    case gl.INVALID_ENUM:
      return 'Invalid enum' as WebGLErrorReason;
    case gl.INVALID_VALUE:
      return 'Invalid value' as WebGLErrorReason;
    case gl.INVALID_OPERATION:
      return 'Invalid operation' as WebGLErrorReason;
    case gl.INVALID_FRAMEBUFFER_OPERATION:
      return 'Invalid framebuffer operation' as WebGLErrorReason;
    case gl.OUT_OF_MEMORY:
      return 'Out of memory' as WebGLErrorReason;
    case gl.CONTEXT_LOST_WEBGL:
      return 'WebGL context lost' as WebGLErrorReason;
    default:
      return `Unknown code 0x${errorCode.toString(16)}` as WebGLErrorReason;
  }
}

export function getWebGLFramebufferStatus(
  gl: WebGL2RenderingContext,
  status: number,
): WebGLErrorReason {
  switch (status) {
    case gl.FRAMEBUFFER_COMPLETE:
      return 'Framebuffer complete' as WebGLErrorReason;
    case gl.FRAMEBUFFER_INCOMPLETE_ATTACHMENT:
      return 'Framebuffer incomplete attachment' as WebGLErrorReason;
    case gl.FRAMEBUFFER_INCOMPLETE_MISSING_ATTACHMENT:
      return 'Framebuffer incomplete missing attachment' as WebGLErrorReason;
    case gl.FRAMEBUFFER_INCOMPLETE_DIMENSIONS:
      return 'Framebuffer incomplete dimensions' as WebGLErrorReason;
    case gl.FRAMEBUFFER_UNSUPPORTED:
      return 'Framebuffer unsupported' as WebGLErrorReason;
    case gl.FRAMEBUFFER_INCOMPLETE_MULTISAMPLE:
      return 'Framebuffer incomplete multisample' as WebGLErrorReason;
    default:
      return `Unknown status 0x${status.toString(16)}` as WebGLErrorReason;
  }
}

/**
 * Throws a WebGLError if the last WebGL call failed.
 *
 * This is can block the main thread, so it should not be called for frequent operations.
 * See {@link https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/WebGL_best_practices#avoid_blocking_api_calls_in_production}.
 *
 * @param gl The WebGL2RenderingContext to check for errors.
 * @param funcName The name of the function that was called, for error reporting.
 */
export function validateLastGlCall(gl: WebGL2RenderingContext, funcName: string) {
  // getError() can only be called ONCE - afterwards it will return NO_ERROR
  const error = gl.getError();
  if (error !== gl.NO_ERROR) {
    throw new WebGLError(getWebGLError(gl, error), funcName);
  }
}

/**
 * Throws a WebGLError if the last WebGL call failed. This is a no-op in production.
 *
 * @param gl The WebGL2RenderingContext to check for errors.
 * @param funcName The name of the function that was called, for error reporting.
 */
export function validateLastGlCallDevelopment(
  gl: WebGL2RenderingContext,
  funcName: string,
) {
  if (process.env.NODE_ENV !== 'production') {
    validateLastGlCall(gl, funcName);
  }
}

export class WebGLError extends BaseError {
  constructor(
    glOrReason: WebGL2RenderingContext | WebGLErrorReason,
    operation: string,
    options?: BaseErrorOptions,
  ) {
    const reason =
      glOrReason instanceof WebGL2RenderingContext
        ? getWebGLError(glOrReason, glOrReason.getError())
        : glOrReason;

    super(
      { name: 'WebGLError', ...options },
      `WebGL Operation failed. Operation: ${operation}. ${reason}`,
    );
  }
}

export class WebGLFramebufferIncompleteError extends BaseError {
  constructor(gl: WebGL2RenderingContext, status: number, options?: BaseErrorOptions) {
    super(
      { name: 'WebGLFramebufferIncompleteError', ...options },
      `WebGL framebuffer was not complete. ${getWebGLFramebufferStatus(gl, status)}`,
    );
  }
}
